import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number | string;
  company_name: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  HSN_SAC?: string;
  GST_SLAB?: string;
  [key: string]: any;
}

interface Customer {
  id: number | string;
  company_name: string;
  city?: string;
  state?: string;
  gst_number?: string;
  gst_uin?: string;
  [key: string]: any;
}

interface InvoiceFromAPI {
  _id?: string;
  Company_id: string | number;
  Customer_id: string | number;
  InvoiceNo: string;
  Date: string;
  Total_Amount: number;
  PaymentStatus?: "Paid" | "Unpaid" | "Partially Paid" | string;
  AmountPaid?: number;
  [key: string]: any;
}

const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Paid", value: "Paid" },
  { label: "Unpaid", value: "Unpaid" },
  { label: "Partially Paid", value: "Partially Paid" }
];

function formatDate(date: string) {
  try {
    return new Date(date).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return date || "";
  }
}

function getCompanyName(companies: Company[], id: string | number) {
  return companies.find((c) => String(c.id) === String(id))?.company_name || "";
}

function getCustomerName(customers: Customer[], id: string | number) {
  return customers.find((c) => String(c.id) === String(id))?.company_name || "";
}

function currency(n: number | string) {
  let num = Number(n) || 0;
  return "₹ " + num.toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

function getStatusBadge(paymentStatus: number | null) {
  if (paymentStatus === 1)
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">
        Paid
      </span>
    );
  if (paymentStatus === 2)
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs font-bold">
        Partial
      </span>
    );
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold">
      Unpaid
    </span>
  );
}

function getPaidPartial(inv: InvoiceFromAPI) {
  if (inv.paid === 1) return currency(inv.Total_Amount);
  if (inv.paid === 2) {
    return (
      <span>
        <span className="text-green-700 font-semibold">{currency(inv.AmountPaid || 0)}</span>{" "}
        <span className="text-gray-500">/ {currency(inv.Total_Amount)}</span>
      </span>
    );
  }
  return (
    <span className="text-gray-500">
      {currency(0)} / {currency(inv.Total_Amount)}
    </span>
  );
}

const Dashboard: React.FC = () => {
  // Data states
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoices, setInvoices] = useState<InvoiceFromAPI[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string>("");
  // Filtering
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchInvoiceNo, setSearchInvoiceNo] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/auth/companies`),
      axios.get(`${API_URL}/auth/customers`),
      axios.get(`${API_URL}/auth/invoices`),
    ])
      .then(([cRes, cuRes, iRes]) => {
        setCompanies(cRes.data || []);
        setCustomers(cuRes.data || []);
        setInvoices(iRes.data || []);
        setFetchError("");
      })
      .catch(() => setFetchError("Could not fetch dashboard data."))
      .finally(() => setLoading(false));
  }, []);

  const summary = useMemo(() => {
    let total = invoices.length;
    let paid = 0,
      unpaid = 0,
      partial = 0,
      paidAmount = 0,
      unpaidAmount = 0,
      partialAmount = 0;

    invoices.forEach((inv) => {
      if (inv.paid === 1) {
        paid++;
        paidAmount += inv.Total_Amount;
      } else if (inv.paid === 2) {
        partial++;
        const paidPart = Number(inv.AmountPaid) || 0;
        paidAmount += paidPart;
        partialAmount += inv.Total_Amount - paidPart;
      } else {
        unpaid++;
        unpaidAmount += inv.Total_Amount;
      }
    });

    return {
      total,
      paid,
      unpaid,
      partial,
      paidAmount,
      unpaidAmount,
      partialAmount,
      totalAmount: invoices.reduce((s, i) => s + (i.Total_Amount || 0), 0),
    };
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    let filtered = invoices;
    if (statusFilter) {
      filtered = filtered.filter(
        (inv) => (inv.PaymentStatus || "Unpaid") === statusFilter
      );
    }
    if (searchInvoiceNo) {
      filtered = filtered.filter((inv) =>
        inv.InvoiceNo.toLowerCase().includes(searchInvoiceNo.trim().toLowerCase())
      );
    }
    return [...filtered].sort(
      (a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime()
    );
  }, [invoices, statusFilter, searchInvoiceNo]);

  return (
    <div className="w-full min-h-screen bg-gradient-to-tr from-indigo-50 to-white py-10 px-2 sm:px-4">
      <header className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-4xl font-bold text-indigo-800 tracking-tight">
              Invoice Dashboard
            </h1>
            <p className="text-gray-500 text-lg mt-1">
              View, search, and track your company invoices at a glance.
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-6 md:mt-0">
            <a
              href="/InvoiceCreator"
              className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2 rounded-lg shadow transition"
            >
              <svg className="mr-2" width="20" height="20" fill="currentColor" viewBox="0 0 20 20"><path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"/></svg>
              New Invoice
            </a>
          </div>
        </div>
      </header>

      {/* Summary cards */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div className="relative bg-white shadow-md rounded-xl px-5 py-6 flex flex-col items-start border-t-4 border-indigo-400">
          <div className="text-gray-500 mb-1 font-medium">Total Invoices</div>
          <span className="text-2xl font-black text-indigo-700">{summary.total}</span>
          <span className="text-gray-400 text-sm mt-2">Amount: {currency(summary.totalAmount)}</span>
        </div>
        <div className="relative bg-white shadow-md rounded-xl px-5 py-6 flex flex-col items-start border-t-4 border-green-400">
          <div className="text-gray-500 mb-1 font-medium">Paid</div>
          <span className="text-2xl font-black text-green-600">{summary.paid}</span>
          <span className="text-gray-400 text-sm mt-2">Amount: {currency(summary.paidAmount)}</span>
        </div>
        <div className="relative bg-white shadow-md rounded-xl px-5 py-6 flex flex-col items-start border-t-4 border-yellow-400">
          <div className="text-gray-500 mb-1 font-medium">Partially Paid</div>
          <span className="text-2xl font-black text-yellow-700">{summary.partial}</span>
          <span className="text-gray-400 text-sm mt-2">Outstanding: {currency(summary.partialAmount)}</span>
        </div>
        <div className="relative bg-white shadow-md rounded-xl px-5 py-6 flex flex-col items-start border-t-4 border-red-400">
          <div className="text-gray-500 mb-1 font-medium">Unpaid</div>
          <span className="text-2xl font-black text-red-700">{summary.unpaid}</span>
          <span className="text-gray-400 text-sm mt-2">Amount: {currency(summary.unpaidAmount)}</span>
        </div>
      </section>

      {/* Controls */}
      <section className="flex flex-col md:flex-row md:items-end md:justify-between mb-4 gap-8">
        <div className="flex gap-4 items-end">
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-1">
              Filter Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-40 px-3 py-2 rounded-lg border border-indigo-300 shadow focus:ring-2 focus:ring-indigo-300 outline-none transition text-gray-800"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-1">
              Search Invoice No.
            </label>
            <input
              type="text"
              placeholder="Search Invoice No..."
              className="w-48 px-3 py-2 rounded-lg border border-gray-300 shadow focus:ring-2 focus:ring-indigo-200 outline-none transition text-gray-800"
              value={searchInvoiceNo}
              onChange={(e) => setSearchInvoiceNo(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="relative">
        <div className="overflow-x-auto bg-white rounded-2xl shadow border border-gray-200">
          <table className="min-w-full">
            <thead>
              <tr className="bg-indigo-50 select-none">
                <th className="py-3 px-2 text-indigo-900 font-bold text-left">#</th>
                <th className="py-3 px-2 text-indigo-900 font-bold text-left whitespace-nowrap">
                  Invoice No.
                </th>
                <th className="py-3 px-2 text-indigo-900 font-bold text-left">Date</th>
                <th className="py-3 px-2 text-indigo-900 font-bold text-left">Company</th>
                <th className="py-3 px-2 text-indigo-900 font-bold text-left">Customer</th>
                <th className="py-3 px-2 text-indigo-900 font-bold text-left">Amount</th>
                <th className="py-3 px-2 text-indigo-900 font-bold text-left">Paid</th>
                <th className="py-3 px-2 text-indigo-900 font-bold text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-indigo-400">
                    Loading invoices...
                  </td>
                </tr>
              ) : filteredInvoices && filteredInvoices.length > 0 ? (
                filteredInvoices.map((inv, idx) => (
                  <tr
                    key={inv._id || inv.InvoiceNo || idx}
                    className={
                      "transition hover:bg-indigo-50 " +
                      (idx % 2 ? "bg-gray-50" : "")
                    }
                  >
                    <td className="py-2 px-2 font-medium">{idx + 1}</td>
                    <td className="py-2 px-2 font-semibold text-indigo-700">
                      {inv.InvoiceNo}
                    </td>
                    <td className="py-2 px-2">{formatDate(inv.Date)}</td>
                    <td className="py-2 px-2">
                      {getCompanyName(companies, inv.Company_id)}
                    </td>
                    <td className="py-2 px-2">
                      {getCustomerName(customers, inv.Customer_id)}
                    </td>
                    <td className="py-2 px-2">{currency(inv.Total_Amount)}</td>
                    <td className="py-2 px-2">{getPaidPartial(inv)}</td>
                    <td className="py-2 px-2">{getStatusBadge(inv.paid)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-400">
                    No invoices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Error alert */}
      {!!fetchError && (
        <div className="mt-7 mx-auto max-w-xl bg-red-50 border border-red-200 rounded-lg px-6 py-3 text-red-800 text-center font-semibold shadow">
          {fetchError}
        </div>
      )}

      {/* Dashboard illustration/help */}
      <div className="flex flex-col md:flex-row justify-between items-center pt-12 gap-4">
        <div className="mb-6 md:mb-0">
          <h3 className="text-lg font-bold text-indigo-900 mb-1">Get Started with Invoices</h3>
          <p className="text-gray-600 mb-2">
            Easily create, manage, and track invoices for your business.
          </p>
          <a
            href="/InvoiceCreator"
            className="inline-block bg-gradient-to-r from-indigo-600 to-indigo-400 text-white px-6 py-2 rounded-lg shadow hover:from-indigo-500 hover:to-indigo-300 font-semibold transition"
          >
            Go to Invoice Generator
          </a>
        </div>
        <div className="flex-1 flex justify-center md:justify-end items-center">
          <img
            src="https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
            alt="dashboard"
            className="w-28 h-28 opacity-90 rounded-full shadow-lg"
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;